import {
  AdminAffiliateClientsResponse,
  AdminAffiliateDetailResponse,
  AdminMarkCommissionsPaidResponse,
  AffiliateAccountStatus,
  AffiliateAccountWithUser,
  AffiliateClientLinkType,
  AffiliateCommissionTotals,
  AffiliateCommissionWithDetails,
} from '@avanzu/types';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PaperSection from '../../../components/PaperSection';
import { apiClient } from '../../../services/apiClient';
import formatDate, { DateFormatOptions } from '../../../utils/formatDate';
import { statusChipColor } from './AffiliatesList';

interface ClientRow {
  organisationId: string;
  orgName: string;
  linkType: AffiliateClientLinkType;
  linkedAt: string;
  hasPaidProject: boolean;
}

const formatCents = (cents: number, currency: string) =>
  `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;

export default function AffiliateView() {
  const { affiliateAccountId } = useParams<{ affiliateAccountId: string }>();
  const navigate = useNavigate();

  const [affiliate, setAffiliate] = useState<AffiliateAccountWithUser | null>(null);
  const [totals, setTotals] = useState<AffiliateCommissionTotals | null>(null);
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [commissions, setCommissions] = useState<AffiliateCommissionWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Settings form state (percent strings for the inputs)
  const [referralCode, setReferralCode] = useState('');
  const [commissionPercent, setCommissionPercent] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');

  const fetchAll = useCallback(async () => {
    if (!affiliateAccountId) return;
    setLoading(true);
    setError(null);
    try {
      const [detailRes, clientsRes, commissionsRes] = await Promise.all([
        apiClient.get(`/admin/affiliates/${affiliateAccountId}`),
        apiClient.get(`/admin/affiliates/${affiliateAccountId}/clients`),
        apiClient.get(`/admin/affiliates/${affiliateAccountId}/commissions`),
      ]);

      if (!detailRes.ok) {
        throw new Error('Failed to load affiliate');
      }

      const detail: AdminAffiliateDetailResponse = await detailRes.json();
      setAffiliate(detail.affiliate);
      setTotals(detail.totals);
      setReferralCode(detail.affiliate.referralCode);
      setCommissionPercent((detail.affiliate.commissionRateBps / 100).toString());
      setDiscountPercent((detail.affiliate.discountRateBps / 100).toString());

      if (clientsRes.ok) {
        const clientsData: AdminAffiliateClientsResponse = await clientsRes.json();
        setClients(clientsData.clients);
      }
      if (commissionsRes.ok) {
        const commissionsData = (await commissionsRes.json()) as {
          commissions: AffiliateCommissionWithDetails[];
        };
        setCommissions(commissionsData.commissions);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching affiliate:', err);
    } finally {
      setLoading(false);
    }
  }, [affiliateAccountId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const runAction = async (fn: () => Promise<Response>, successMessage: string) => {
    setActionLoading(true);
    setError(null);
    setInfo(null);
    try {
      const response = await fn();
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || data.message || 'Action failed');
      }
      setInfo(successMessage);
      await fetchAll();
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = () =>
    runAction(
      () => apiClient.put(`/admin/affiliates/${affiliateAccountId}/approve`, {}),
      'Affiliate approved — Stripe coupon and promotion code issued'
    );

  const handleSuspend = () =>
    runAction(
      () => apiClient.put(`/admin/affiliates/${affiliateAccountId}/suspend`, {}),
      'Affiliate suspended — promotion code deactivated'
    );

  const handleDelete = async () => {
    setDeleteDialogOpen(false);
    const result = await runAction(
      () => apiClient.delete(`/admin/affiliates/${affiliateAccountId}`),
      'Affiliate deleted'
    );
    if (result) {
      navigate('/app/affiliates');
    }
  };

  const handleSaveSettings = () => {
    const commissionRateBps = Math.round(parseFloat(commissionPercent) * 100);
    const discountRateBps = Math.round(parseFloat(discountPercent) * 100);
    if (isNaN(commissionRateBps) || isNaN(discountRateBps)) {
      setError('Rates must be numeric percentages');
      return;
    }
    return runAction(
      () =>
        apiClient.put(`/admin/affiliates/${affiliateAccountId}/settings`, {
          referralCode: referralCode.trim().toUpperCase(),
          commissionRateBps,
          discountRateBps,
        }),
      'Settings updated'
    );
  };

  const handleMarkPaid = () =>
    runAction(
      () => apiClient.post(`/admin/affiliates/${affiliateAccountId}/payouts`, {}),
      'Pending commissions marked as paid'
    ).then((data?: AdminMarkCommissionsPaidResponse) => {
      if (data && data.paidCount === 0) {
        setInfo('No pending commissions to pay');
      }
    });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!affiliate) {
    return (
      <PaperSection title="Affiliate">
        <Typography color="error">{error || 'Affiliate not found'}</Typography>
      </PaperSection>
    );
  }

  const fullName = `${affiliate.user.firstName} ${affiliate.user.lastName}`.trim();

  return (
    <Stack spacing={3}>
      <PaperSection title={affiliate.businessName || fullName || affiliate.user.email}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {info && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {info}
          </Alert>
        )}

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Chip
            label={affiliate.status}
            color={statusChipColor(affiliate.status)}
            size="small"
          />
          <Typography variant="body2" color="text.secondary">
            Registered {formatDate(affiliate.createdAt, DateFormatOptions.DATEONLY)}
          </Typography>
        </Stack>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">
              Contact
            </Typography>
            <Typography variant="body2">{fullName || '-'}</Typography>
            <Typography variant="body2">{affiliate.user.email}</Typography>
            <Typography variant="body2">{affiliate.phone || ''}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">
              Website
            </Typography>
            <Typography variant="body2">{affiliate.website || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">
              Linked clients
            </Typography>
            <Typography variant="body2">{affiliate.clientCount}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">
              Stripe coupon
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {affiliate.stripeCouponId || '-'}
            </Typography>
          </Grid>
        </Grid>

        <Stack direction="row" spacing={2}>
          {affiliate.status !== AffiliateAccountStatus.APPROVED && (
            <Button variant="contained" color="success" onClick={handleApprove} disabled={actionLoading}>
              Approve
            </Button>
          )}
          {affiliate.status === AffiliateAccountStatus.APPROVED && (
            <Button variant="outlined" color="error" onClick={handleSuspend} disabled={actionLoading}>
              Suspend
            </Button>
          )}
          {affiliate.status === AffiliateAccountStatus.SUSPENDED && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={actionLoading}
            >
              Delete
            </Button>
          )}
        </Stack>
      </PaperSection>

      <PaperSection title="Settings">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              label="Referral code"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              size="small"
              fullWidth
              inputProps={{ style: { fontFamily: 'monospace' } }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Commission %"
              value={commissionPercent}
              onChange={(e) => setCommissionPercent(e.target.value)}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Discount % (first project)"
              value={discountPercent}
              onChange={(e) => setDiscountPercent(e.target.value)}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button variant="contained" onClick={handleSaveSettings} disabled={actionLoading}>
              Save
            </Button>
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Changing the code or discount recreates the Stripe promotion code / coupon. Commission
          and discount both apply to a referred organisation's first paid project only.
        </Typography>
      </PaperSection>

      {totals && (
        <PaperSection title="Commissions">
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Pending
              </Typography>
              <Typography variant="h6">
                {formatCents(totals.pendingAmount, totals.currency)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Paid
              </Typography>
              <Typography variant="h6">{formatCents(totals.paidAmount, totals.currency)}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Reversed
              </Typography>
              <Typography variant="h6">
                {formatCents(totals.reversedAmount, totals.currency)}
              </Typography>
            </Grid>
          </Grid>

          {totals.pendingAmount > 0 && (
            <Button
              variant="contained"
              onClick={handleMarkPaid}
              disabled={actionLoading}
              sx={{ mb: 2 }}
            >
              Mark pending as paid
            </Button>
          )}

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Organisation</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Charged</TableCell>
                  <TableCell>Commission</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Earned</TableCell>
                  <TableCell>Paid</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {commissions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No commissions yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  commissions.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>{c.organisationName}</TableCell>
                      <TableCell>{c.projectName || '-'}</TableCell>
                      <TableCell>{formatCents(c.grossAmount, c.currency)}</TableCell>
                      <TableCell>{formatCents(c.commissionAmount, c.currency)}</TableCell>
                      <TableCell>
                        <Chip
                          label={c.status}
                          size="small"
                          color={
                            c.status === 'PAID'
                              ? 'success'
                              : c.status === 'REVERSED'
                                ? 'error'
                                : 'warning'
                          }
                        />
                      </TableCell>
                      <TableCell>{formatDate(c.createdAt, DateFormatOptions.DATEONLY)}</TableCell>
                      <TableCell>
                        {c.paidAt ? formatDate(c.paidAt, DateFormatOptions.DATEONLY) : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </PaperSection>
      )}

      <PaperSection title="Linked clients">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Organisation</TableCell>
                <TableCell>Link type</TableCell>
                <TableCell>First paid project</TableCell>
                <TableCell>Linked</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No linked organisations
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                clients.map((client) => (
                  <TableRow
                    key={client.organisationId}
                    onClick={() => navigate(`/app/organisations/${client.organisationId}`)}
                    sx={{
                      '&:hover': { backgroundColor: 'action.hover', cursor: 'pointer' },
                    }}
                  >
                    <TableCell>{client.orgName}</TableCell>
                    <TableCell>
                      <Chip label={client.linkType} size="small" />
                    </TableCell>
                    <TableCell>{client.hasPaidProject ? 'Yes' : 'Not yet'}</TableCell>
                    <TableCell>{formatDate(client.linkedAt, DateFormatOptions.DATEONLY)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </PaperSection>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete affiliate?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently removes the affiliate account, its client links and commission
            history. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
